import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'fixarr-activity-log-v1';
const MAX_ENTRIES = 500; // Mantener últimos 500 eventos
const RETENTION_DAYS = 30; // Limpiar eventos de más de 30 días

/**
 * Hook para manejar el historial de actividad del roadmap
 * Similar a un "git log" para tracking de progreso
 */
const useActivityLog = () => {
  const [activities, setActivities] = useState([]);

  // Cargar actividades al montar
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setActivities(parsed);
      } catch (e) {
        console.error('Error loading activity log:', e);
        setActivities([]);
      }
    }
  }, []);

  // Guardar en localStorage
  const saveActivities = useCallback((newActivities) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newActivities));
    setActivities(newActivities);
  }, []);

  /**
   * Registrar una nueva actividad
   * @param {Object} activity - { type, action, taskId, taskName, details }
   */
  const logActivity = useCallback((activity) => {
    const newEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...activity
    };

    setActivities(prev => {
      const updated = [newEntry, ...prev].slice(0, MAX_ENTRIES);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });

    return newEntry;
  }, []);

  /**
   * Obtener actividades recientes
   * @param {number} limit - Número máximo de actividades
   */
  const getRecentActivity = useCallback((limit = 20) => {
    return activities.slice(0, limit);
  }, [activities]);

  /**
   * Obtener actividades de una tarea específica
   * @param {string} taskId - ID de la tarea
   */
  const getActivityByTask = useCallback((taskId) => {
    return activities.filter(a => a.taskId === taskId);
  }, [activities]);

  /**
   * Obtener actividades por rango de fecha
   * @param {string} range - 'today', 'week', 'month', 'all'
   */
  const getActivityByDate = useCallback((range = 'all') => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let cutoff;
    switch (range) {
      case 'today':
        cutoff = startOfDay;
        break;
      case 'week':
        cutoff = new Date(startOfDay.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        cutoff = new Date(startOfDay.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        return activities;
    }

    return activities.filter(a => new Date(a.timestamp) >= cutoff);
  }, [activities]);

  /**
   * Obtener actividades agrupadas por día
   */
  const getActivityGroupedByDay = useCallback(() => {
    const groups = {};
    
    activities.forEach(activity => {
      const date = new Date(activity.timestamp);
      const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
      
      if (!groups[dayKey]) {
        groups[dayKey] = [];
      }
      groups[dayKey].push(activity);
    });

    // Convertir a array ordenado
    return Object.entries(groups)
      .map(([date, items]) => ({ date, items }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [activities]);

  /**
   * Obtener estadísticas de actividad
   */
  const getStats = useCallback(() => {
    const today = getActivityByDate('today');
    const week = getActivityByDate('week');
    
    const checklistCompleted = (arr) => 
      arr.filter(a => a.type === 'checklist_item' && a.action === 'completed').length;
    
    const statusChanges = (arr) =>
      arr.filter(a => a.type === 'status_change').length;

    const observations = (arr) =>
      arr.filter(a => a.type === 'observation').length;

    return {
      today: {
        total: today.length,
        checklistCompleted: checklistCompleted(today),
        statusChanges: statusChanges(today),
        observations: observations(today)
      },
      week: {
        total: week.length,
        checklistCompleted: checklistCompleted(week),
        statusChanges: statusChanges(week),
        observations: observations(week)
      },
      allTime: {
        total: activities.length,
        checklistCompleted: checklistCompleted(activities),
        statusChanges: statusChanges(activities),
        observations: observations(activities)
      }
    };
  }, [activities, getActivityByDate]);

  /**
   * Limpiar actividades antiguas (>30 días)
   */
  const clearOldActivity = useCallback(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - RETENTION_DAYS);

    const filtered = activities.filter(a => new Date(a.timestamp) >= cutoff);
    saveActivities(filtered);
    
    return activities.length - filtered.length; // Retorna cuántos se eliminaron
  }, [activities, saveActivities]);

  /**
   * Limpiar todo el historial
   */
  const clearAll = useCallback(() => {
    saveActivities([]);
  }, [saveActivities]);

  /**
   * Contar actividades no vistas (para badge)
   */
  const getUnseenCount = useCallback(() => {
    const lastSeen = localStorage.getItem('fixarr-activity-last-seen');
    if (!lastSeen) return activities.length > 0 ? Math.min(activities.length, 99) : 0;
    
    return activities.filter(a => new Date(a.timestamp) > new Date(lastSeen)).length;
  }, [activities]);

  /**
   * Marcar actividades como vistas
   */
  const markAsSeen = useCallback(() => {
    localStorage.setItem('fixarr-activity-last-seen', new Date().toISOString());
  }, []);

  return {
    activities,
    logActivity,
    getRecentActivity,
    getActivityByTask,
    getActivityByDate,
    getActivityGroupedByDay,
    getStats,
    clearOldActivity,
    clearAll,
    getUnseenCount,
    markAsSeen
  };
};

export default useActivityLog;
